// SURGE TRANSFORMATION BURST (owner 2026-11-03: 'update the animations of the transformations and the auras to show energy
// and light and force shooting outward as it transforms'). Render-only, driven by the sim's transform event:
//   1. charge-in (0.2 s at I .. 0.41 s at VIII, the same build the ignition sting uses): motes sucked INWARD from a ring
//      around the hero;
//   2. release: light rays bursting out of the body in every direction (one instanced additive quad pool, camera-facing,
//      each ray shoots out fast, thins and fades), an expanding radial glare, several force rings racing outward along the
//      ground and in the air, dust / debris / wind kicked outward (BlastFx wind swirl + outward motes), electric arcs
//      from the body to the ground around it (fx.arcs), nearby foes DRAWN shoved outward (pushAt, sim untouched), and the
//      view adds the camera kick, hit-stop and screen-lighting flash (onRelease);
//   3. the steady aura (SurgeVisual / AuraV3), with periodic outward pulses (pulse(): a small wave + a few ray flickers)
//      every ~1.3 s at I down to ~0.4 s at VIII, so the aura keeps pouring energy out.
// Everything escalates with the form's intensity (I 0.12 .. VIII 1: more rays, longer reach, more rings and arcs, a
// bigger glare); VIII fills the screen. Each form's own palette; Reduce flashing (FLASH budget / dim), particle density
// (the view's quality multiplier), Glow (emission gain) and Camera shake (the view) all apply. Pooled: no per-frame
// allocation once built; prewarm's rehearsal fires every form so the ray material is compiled before a run.
import * as THREE from 'three/webgpu';
import { abs, oneMinus, pow, uv } from 'three/tsl';
import { FLASH, type Fx } from './fx';
import { formGlow, surgeForm } from '../core/surgeForms';

const RAYS = 64;
/** charge-in seconds before the release for form `tier` (the ignition sting's build uses the same curve) */
export const surgeChargeIn = (tier: number): number => 0.2 + 0.03 * (Math.max(1, Math.min(8, tier)) - 1);

interface Ray { on: boolean; t: number; life: number; ang: number; len: number; w: number; k: number; r: number; g: number; b: number }
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _z = new THREE.Vector3(0, 0, 1);
const _c = new THREE.Color();

export class SurgeBurst {
  readonly mesh: THREE.InstancedMesh;
  private rays: Ray[] = [];
  private queue: { at: number; fn: () => void }[] = [];
  private t = 0;
  /** drawn foe shove: strength envelope, reach (m), centre, scale */
  private push = 0; private pushR = 0; private cx = 0; private cz = 0; private pushK = 1;
  /** extra light the view adds while a pulse / release rings (0..1) */
  glow = 0;
  glowOn = true;
  /** particle-density multiplier (the view's quality preset) */
  density = 1;
  /** the view's hook at the release: camera kick, hit-stop, screen-lighting flash */
  onRelease: ((I: number, x: number, y: number, z: number, tier: number) => void) | null = null;

  constructor(scene: THREE.Scene) {
    const geo = new THREE.PlaneGeometry(1, 1, 1, 1); geo.translate(0.5, 0, 0); // the ray's root at x = 0, tip at x = 1
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false, side: THREE.DoubleSide });
    // bright at the root, fading to the tip; soft edges across
    m.opacityNode = pow(oneMinus(uv().x), 1.6).mul(pow(oneMinus(abs(uv().y.mul(2).sub(1))), 0.9));
    this.mesh = new THREE.InstancedMesh(geo, m, RAYS);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(RAYS * 3), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 8; this.mesh.name = 'surge-burst-rays';
    for (let i = 0; i < RAYS; i++) this.rays.push({ on: false, t: 0, life: 1, ang: 0, len: 1, w: 0.1, k: 1, r: 1, g: 1, b: 1 });
    scene.add(this.mesh);
  }

  /** fire `n` rays from the current centre: length L (m), width w, life s, brightness k, colour core -> mid along the fan */
  private fan(n: number, L: number, w: number, life: number, k: number, core: number, mid: number): void {
    let left = n;
    for (const r of this.rays) {
      if (left <= 0) break;
      if (r.on) continue;
      left--;
      r.on = true; r.t = 0; r.life = life * (0.7 + Math.random() * 0.6); r.ang = Math.random() * Math.PI * 2;
      r.len = L * (0.45 + Math.random() * 0.75); r.w = w * (0.6 + Math.random() * 0.8); r.k = k;
      _c.setHex(Math.random() < 0.45 ? core : mid); r.r = _c.r; r.g = _c.g; r.b = _c.b;
    }
  }

  /** the transformation into `tier` at the hero centre (x, y, z), ground height gy: charge-in, then the release */
  transform(tier: number, x: number, y: number, z: number, gy: number, fx: Fx, W: number): void {
    const f = surgeForm(tier); if (!f) return;
    const I = f.intensity, { hot: core, mid } = formGlow(f), acc = f.accent, q = this.density, dim = FLASH.dim;
    const chargeS = surgeChargeIn(tier), t0 = this.t;
    this.mesh.position.set(x, y, z); this.cx = x; this.cz = z;
    // ---- 1. charge-in: motes drawn inward from a ring (three waves over the build), a brief tightening glare
    const suck = (k: number) => {
      const n = Math.round((10 + I * 22) * q);
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.283, R = (2 + I * 2.5 + Math.random() * 1.2) * W, yy = y + (Math.random() - 0.4) * 2 * W, sp = R / Math.max(0.12, chargeS * (1 - k * 0.5)) * 1.1;
        fx.particles.emitDir(x + Math.cos(a) * R, yy, z + Math.sin(a) * R, -Math.cos(a) * sp, (y - yy) / Math.max(0.12, chargeS), -Math.sin(a) * sp, i % 2 ? mid : core, 0.22 * W, chargeS * (1 - k * 0.4), 2.2 * dim);
      }
    };
    suck(0); this.queue.push({ at: t0 + chargeS * 0.35, fn: () => suck(0.35) }, { at: t0 + chargeS * 0.65, fn: () => suck(0.65) });
    // ---- 2. release
    this.queue.push({ at: t0 + chargeS, fn: () => {
      const big = FLASH.big(), gain = (this.glowOn ? 2.6 : 1.7) * big;
      this.mesh.position.set(x, y, z);
      // light rays: a 360-degree fan, a few long ones reaching far (VIII: across the screen)
      this.fan(Math.round((18 + I * 40) * (0.5 + 0.5 * q)), (2.4 + I * 6) * W, (0.09 + I * 0.2) * W, 0.32 + I * 0.28, gain, core, mid);
      this.fan(Math.round(4 + I * 10), (5 + I * 14) * W, (0.05 + I * 0.08) * W, 0.4 + I * 0.3, gain * 0.8, core, core);
      // radial glare
      fx.flashes.add(x, y, z, (1.3 + I * 2.8) * W, 0.22 + I * 0.12, core, (0.7 + I * 0.7) * dim);
      // force rings: along the ground and in the air, racing outward one after another
      const nr = 2 + Math.round(I * 5);
      for (let k = 0; k < nr; k++) {
        const at = this.t + k * 0.06, R = (4 + I * 10) * (1 + k * 0.3) * W, life = 0.5 + I * 0.25, col = k % 2 ? mid : core;
        this.queue.push({ at, fn: () => { fx.rings.add(x, gy + 0.15, z, R, life, col, 1.1 * dim, 1); if (k % 2 === 1) fx.rings.add(x, y + 0.6 + (k >> 1) * 0.7 * W, z, R * 0.55, life * 0.8, acc, 0.9 * dim, 0.6); } });
      }
      // dust, debris and wind outward; outward motes
      fx.blast?.windSwirl(x, z, 1 + Math.round(I * 3));
      const motes = Math.round((24 + I * 70) * q);
      for (let k = 0; k < motes; k++) {
        const a = Math.random() * 6.283, r = 0.4 + Math.random() * (0.8 + I), sp = (4 + I * 6) * (0.6 + Math.random() * 0.8) * W;
        fx.particles.emitDir(x + Math.cos(a) * r, y + (Math.random() - 0.5) * 2 * W, z + Math.sin(a) * r, Math.cos(a) * sp, (1 + Math.random() * 3) * W, Math.sin(a) * sp, k % 3 ? mid : core, 0.3 * W, 0.6 + I * 0.4, 2.5 * dim);
      }
      // electric arcs shooting out to the ground around the hero (the form's accent colour), a ring where each one lands
      const na = Math.round((3 + I * 9) * (0.5 + 0.5 * q)), bglow = (this.glowOn ? 3.4 : 2.2) * dim;
      for (let k = 0; k < na; k++) {
        const a = (k / na) * 6.283 + Math.random() * 0.5, R = (2 + I * 4 + Math.random() * 1.5) * W, gx = x + Math.cos(a) * R, gz = z + Math.sin(a) * R;
        this.queue.push({ at: this.t + Math.random() * 0.12, fn: () => {
          fx.arcs.addChain([x, y + 0.2, z, (x + gx) * 0.5 + (Math.random() - 0.5) * 0.8, y + 0.3 + Math.random() * 0.8 * W, (z + gz) * 0.5 + (Math.random() - 0.5) * 0.8, gx, gy, gz], acc, 0.8 + I * 1.1, 0.12 + I * 0.08, 0.35 * W, bglow);
          if (I >= 0.5) fx.rings.add(gx, gy + 0.05, gz, 0.9 * W, 0.25, acc, 1.2 * dim, 0.6);
        } });
      }
      // foes drawn shoved outward (sim untouched), the view's camera kick / hit-stop / flash
      this.push = 1; this.pushR = (4 + I * 8) * W; this.pushK = 0.6 + I * 1.4;
      this.glow = Math.max(this.glow, 0.6 + I * 0.6);
      this.onRelease?.(I, x, y, z, tier);
    } });
  }

  /** an outward pulse while the aura is live: a small energy wave along the ground + a few ray flickers */
  pulse(tier: number, x: number, y: number, z: number, gy: number, fx: Fx, W: number): void {
    const f = surgeForm(tier); if (!f) return;
    const I = f.intensity, dim = FLASH.dim, gain = (this.glowOn ? 1.6 : 1.1) * dim * (0.6 + I * 0.6);
    this.mesh.position.set(x, y, z);
    const gl = formGlow(f);
    this.fan(Math.round((4 + I * 9) * (0.5 + 0.5 * this.density)), (0.9 + I * 1.8) * W, (0.05 + I * 0.08) * W, 0.22 + I * 0.1, gain, gl.hot, gl.mid);
    fx.rings.add(x, gy + 0.12, z, (2.5 + I * 3.5) * W, 0.4 + I * 0.15, gl.mid, 0.7 * dim, 0.6);
    if (I >= 0.6) fx.rings.add(x, y + 0.5, z, (1.6 + I * 1.4) * W, 0.35, f.accent, 0.6 * dim, 0.5);
    this.glow = Math.max(this.glow, 0.25 + I * 0.35);
  }

  /** drawn-only outward nudge (m) for a foe at (x, z) during a release; false when nothing is pushing */
  pushAt(x: number, z: number, out: { x: number; z: number }): boolean {
    if (this.push <= 0.001) return false;
    const dx = x - this.cx, dz = z - this.cz, dist = Math.hypot(dx, dz);
    if (dist >= this.pushR || dist < 1e-3) return false;
    const f = this.push * this.pushK * (1 - dist / this.pushR) / dist;
    out.x = dx * f; out.z = dz * f; return true;
  }

  update(dt: number, camera: THREE.Camera): void {
    this.t += dt;
    if (this.queue.length) { for (let i = 0; i < this.queue.length; i++) if (this.queue[i].at <= this.t) { const q = this.queue[i]; this.queue.splice(i, 1); i--; q.fn(); } }
    if (this.push > 0) this.push = Math.max(0, this.push - dt / 0.45);
    if (this.glow > 0) this.glow = Math.max(0, this.glow - dt / 0.35);
    // rays: camera-facing fan at the hero centre
    this.mesh.quaternion.copy(camera.quaternion);
    const col = this.mesh.instanceColor!.array as Float32Array;
    let n = 0;
    for (const r of this.rays) {
      if (!r.on) continue;
      r.t += dt; if (r.t >= r.life) { r.on = false; continue; }
      const p = r.t / r.life, grow = 1 - Math.pow(1 - p, 3), fade = Math.pow(1 - p, 1.3);
      _q.setFromAxisAngle(_z, r.ang); _s.set(r.len * grow, r.w * (1 - p * 0.6), 1); _p.set(0, 0, 0);
      this.mesh.setMatrixAt(n, _m.compose(_p, _q, _s));
      col[n * 3] = r.r * r.k * fade; col[n * 3 + 1] = r.g * r.k * fade; col[n * 3 + 2] = r.b * r.k * fade;
      n++;
    }
    this.mesh.count = n; this.mesh.visible = n > 0;
    if (n) { this.mesh.instanceMatrix.needsUpdate = true; this.mesh.instanceColor!.needsUpdate = true; }
  }

  clear(): void { this.queue.length = 0; this.push = 0; this.glow = 0; for (const r of this.rays) r.on = false; this.mesh.count = 0; this.mesh.visible = false; }
}
